import { ruCountryBy, ruCountryTo } from '@/i18n/countryCases';

/**
 * UI-тексты сравнительных ledger-таблиц (GfServiceLedger) для вертикалей
 * VPN / Insurance / Transport / Tours / Stay — собираются в ledgerConfigs.js.
 * Здесь — ТОЛЬКО подлинные UI-лейблы: заголовки колонок, all-лейблы свитчера,
 * лейблы scope, тексты note, selectLabel, лейблы фильтров и человекочитаемые
 * карты имён (категории/тип группы/длительность/язык/способ транспорта).
 * Демо-данные (названия маршрутов, тарифов, туров, брендов) остаются инлайн
 * в ledgerConfigs.js. Интерполируемые строки — функции.
 */
export const ledgerLabels = {
    en: {
        // Generic column headers
        col: {
            plan: 'Plan',
            devices: 'Devices',
            pricePerMo: 'Price / mo',
            coverage: 'Coverage',
            pricePerDay: 'Price / day',
            route: 'Route',
            time: 'Time',
            price: 'Price',
            tour: 'Tour',
            city: 'City',
            type: 'Type',
            length: 'Length',
            cityStay: 'City',
            options: 'Options',
            priceLevel: 'Price level',
        },
        // Operator/mode switcher "all" labels
        all: {
            services: 'All services',
            modes: 'All modes',
            types: 'All types',
        },
        // Scope tab labels
        scope: {
            monthly: 'Monthly',
            yearly: 'Yearly',
            country: 'Country',
            schengen: 'Schengen',
            domestic: 'Domestic',
            crossBorder: 'Cross-border',
        },
        // Footnotes under the ledger (interpolated → functions)
        note: {
            vpnLocal: 'Monthly price billed monthly',
            vpnRegional: 'Monthly price billed yearly',
            insuranceLocal: (countryName) => `Coverage for a trip${countryName ? ` to ${countryName}` : ''}`,
            insuranceRegional: 'Coverage for the Schengen area',
            transportLocal: (countryName) => `Routes within ${countryName || 'the country'}`,
            transportRegional: 'International routes',
            toursLocal: 'From-price per person in a group',
            toursRegional: 'From-price for a private tour',
            toursDemoLocal: 'Price per person in a group',
            toursDemoRegional: 'Price for a private tour',
        },
        // Last-column select button labels
        select: {
            vpn: 'Choose →',
            insurance: 'Get it →',
            transport: 'Book →',
            tours: 'View →',
            stay: 'View options',
        },
        // Per-source (per-platform) tour select label
        selSource: (source) => `View on ${source}`,
        // Per-day rental marker (transport)
        perDay: 'per day',
        // Tour duration not fixed
        flexible: 'flexible',
        // Filters
        filter: {
            tourType: 'Tour type',
            tourTypeAll: 'Any type',
            categories: 'Categories',
            city: 'City',
            cityAll: 'All cities',
            language: 'Language',
            languageAll: 'Any language',
            length: 'Length',
            lengthAll: 'Any',
            platform: 'Platform',
            platformAll: 'All',
            kids: 'Kid-friendly',
            instant: 'Instant booking',
        },
        // Tour duration bucket names (filter options)
        dur: {
            short: 'Under 3 hours',
            half: '3–6 hours',
            long: 'Over 6 hours',
            flex: 'Flexible',
        },
        // Tour category names
        cat: {
            sightseeing: 'Sightseeing',
            nature: 'Nature',
            culture: 'Culture',
            history: 'History',
            food: 'Food',
            adventure: 'Adventure',
            boat_tours: 'Boat',
            other: 'Other',
        },
        // Tour demo-fallback category names
        catDemo: {
            boat: 'Boat',
            nature: 'Nature',
            history: 'History',
            food: 'Food',
        },
        // Tour group-type names
        groupType: {
            group: 'Group',
            private: 'Private',
        },
        // Transport mode names
        mode: {
            bus: 'Bus',
            transfer: 'Transfer',
            ferry: 'Ferry',
            car: 'Rental',
        },
        // Tour conducting-language names (filter options)
        tourLang: {
            en: 'English',
            ru: 'Russian',
            de: 'German',
            es: 'Spanish',
            fr: 'French',
            it: 'Italian',
            pt: 'Portuguese',
            pl: 'Polish',
            tr: 'Turkish',
            ar: 'Arabic',
            hi: 'Hindi',
            ur: 'Urdu',
            ja: 'Japanese',
            ko: 'Korean',
            cmn: 'Chinese',
            el: 'Greek',
            he: 'Hebrew',
            cs: 'Czech',
            nl: 'Dutch',
            sv: 'Swedish',
            da: 'Danish',
            no: 'Norwegian',
            uk: 'Ukrainian',
            sr: 'Serbian',
            hr: 'Croatian',
            hu: 'Hungarian',
            th: 'Thai',
        },
    },
    ru: {
        col: {
            plan: 'Тариф',
            devices: 'Устройства',
            pricePerMo: 'Цена / мес',
            coverage: 'Покрытие',
            pricePerDay: 'Цена / день',
            route: 'Маршрут',
            time: 'Время',
            price: 'Цена',
            tour: 'Тур',
            city: 'Город',
            type: 'Тип',
            length: 'Длит.',
            cityStay: 'Город',
            options: 'Вариантов',
            priceLevel: 'Дороговизна',
        },
        all: {
            services: 'Все сервисы',
            modes: 'Все способы',
            types: 'Все типы',
        },
        scope: {
            monthly: 'Помесячно',
            yearly: 'Годовой',
            country: 'Страна',
            schengen: 'Шенген',
            domestic: 'По стране',
            crossBorder: 'За границу',
        },
        note: {
            vpnLocal: 'Цена за месяц при помесячной оплате',
            vpnRegional: 'Цена за месяц при оплате за год',
            insuranceLocal: (countryName) => `Покрытие для поездки${countryName ? ` ${ruCountryTo(countryName)}` : ''}`,
            insuranceRegional: 'Покрытие для Шенгенской зоны',
            transportLocal: (countryName) => (countryName ? `Маршруты ${ruCountryBy(countryName)}` : 'Маршруты по стране'),
            transportRegional: 'Международные маршруты',
            toursLocal: 'Цена «от» на человека в группе',
            toursRegional: 'Цена «от» за индивидуальный тур',
            toursDemoLocal: 'Цена за человека в группе',
            toursDemoRegional: 'Цена за индивидуальный тур',
        },
        select: {
            vpn: 'Выбрать →',
            insurance: 'Оформить →',
            transport: 'Заказать →',
            tours: 'Смотреть →',
            stay: 'Смотреть варианты',
        },
        selSource: (source) => `Смотреть на ${source}`,
        perDay: 'в сутки',
        flexible: 'по программе',
        filter: {
            tourType: 'Тип тура',
            tourTypeAll: 'Любой тип',
            categories: 'Категории',
            city: 'Город',
            cityAll: 'Все города',
            language: 'Язык',
            languageAll: 'Любой язык',
            length: 'Длительность',
            lengthAll: 'Любая',
            platform: 'Площадка',
            platformAll: 'Все',
            kids: 'Можно с детьми',
            instant: 'Мгновенное бронирование',
        },
        dur: {
            short: 'До 3 часов',
            half: '3–6 часов',
            long: 'Дольше 6 часов',
            flex: 'По программе',
        },
        cat: {
            sightseeing: 'Обзорные',
            nature: 'Природа',
            culture: 'Культура',
            history: 'История',
            food: 'Гастро',
            adventure: 'Приключения',
            boat_tours: 'Морские',
            other: 'Другое',
        },
        catDemo: {
            boat: 'Морские',
            nature: 'Природа',
            history: 'История',
            food: 'Гастро',
        },
        groupType: {
            group: 'Групповой',
            private: 'Индивидуальный',
        },
        mode: {
            bus: 'Автобус',
            transfer: 'Трансфер',
            ferry: 'Паром',
            car: 'Аренда',
        },
        tourLang: {
            en: 'Английский',
            ru: 'Русский',
            de: 'Немецкий',
            es: 'Испанский',
            fr: 'Французский',
            it: 'Итальянский',
            pt: 'Португальский',
            pl: 'Польский',
            tr: 'Турецкий',
            ar: 'Арабский',
            hi: 'Хинди',
            ur: 'Урду',
            ja: 'Японский',
            ko: 'Корейский',
            cmn: 'Китайский',
            el: 'Греческий',
            he: 'Иврит',
            cs: 'Чешский',
            nl: 'Нидерландский',
            sv: 'Шведский',
            da: 'Датский',
            no: 'Норвежский',
            uk: 'Украинский',
            sr: 'Сербский',
            hr: 'Хорватский',
            hu: 'Венгерский',
            th: 'Тайский',
        },
    },
};
